import { Box, TextField, Button, CircularProgress, InputAdornment, Typography, Drawer, IconButton } from "@mui/material";
import React, { useState, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import swal from "sweetalert";
import ServicePrincipal from "../services/ServicePrincipal.js";
import BadgeA6 from "./BadgeA6.jsx";

export const initialData = {
  referenceId: "",
};

export const SearchStatusParticipant = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({ referenceId: false });
  const [openDrawer, setOpenDrawer] = useState(false);
  const [infosParticipant, setInfosParticipant] = useState(null);
  const principalService = new ServicePrincipal();

  // S'ouvre automatiquement quand infosParticipant change
  useEffect(() => {
    if (infosParticipant) {
      setOpenDrawer(true);
    }
  }, [infosParticipant]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
    setErrors({ ...errors, [name]: false });
  };

  const validateFields = () => {
    const newErrors = { referenceId: !data.referenceId.trim() };
    setErrors(newErrors);
    return !Object.values(newErrors).some((e) => e);
  };

  const handleSubmit = async () => {
    setInfosParticipant(null);
    if (!validateFields()) return;
    setLoading(true);

    try {
      const payload = { reference: data.referenceId.trim() };
      const res = await principalService.searchParticipantByReference(payload);

      if (res?.items?.length === 0) {
        swal({
          title: "Identifiant introuvable",
          text: `L'identifiant "${data.referenceId.toUpperCase()}" ne correspond à aucun participant enregistré dans notre base de données. Veuillez vérifier votre référence et réessayer.`,
          icon: "warning",
          button: {
            text: "Réessayer",
            closeModal: true,
          },
        });
      } else if (res?.items && res.items.length > 0) {
        setInfosParticipant(res.items[0]);
      } else {
        swal({
          title: "Erreur de recherche",
          text: "Une erreur s'est produite lors de la recherche. Veuillez réessayer.",
          icon: "error",
          button: {
            text: "Fermer",
            closeModal: true,
          },
        });
      }
    } catch (error) {
      console.error("Erreur lors de la recherche :", error);
      swal({
        title: "Erreur système",
        text: "Une erreur inattendue est survenue. Nos équipes ont été informées. Veuillez réessayer plus tard.",
        icon: "error",
        button: {
          text: "Fermer",
          closeModal: true,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <Box sx={{ maxWidth: 700, margin: "auto" }}>
      <Typography
        variant="subtitle1"
        sx={{
          color: "#555",
          padding: "8px 8px",
          fontWeight: "bold",
          fontSize: "14px",
        }}
      >
        Déjà inscrit? Saisir votre reférence
      </Typography>
      {/* Barre de recherche + bouton sur la même ligne */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0,
            flex: 1,
            minWidth: 250,
            borderRadius: "12px",
            border: errors.referenceId ? "2px solid #d32f2f" : "1px solid #5fa53a",
            overflow: "hidden",
            backgroundColor: "#fff",
            transition: "all 0.3s ease",
            "&:hover": {
              borderColor: errors.referenceId ? "#d32f2f" : "#5fa53a",
              boxShadow: errors.referenceId
                ? "0 2px 8px rgba(211, 47, 47, 0.1)"
                : "0 2px 8px rgba(95, 165, 58, 0.1)",
            },
            "&:focus-within": {
              borderColor: errors.referenceId ? "#d32f2f" : "#5fa53a",
              boxShadow: errors.referenceId
                ? "0 2px 12px rgba(211, 47, 47, 0.2)"
                : "0 2px 12px rgba(95, 165, 58, 0.2)",
            },
          }}
        >
          <TextField
            fullWidth
            name="referenceId"
            placeholder="Saisir votre ID pour avoir votre badge"
            value={data.referenceId}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            error={errors.referenceId}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ ml: 2 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                border: "none",
                "& fieldset": {
                  border: "none",
                },
                "&:hover fieldset": {
                  border: "none",
                },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
                "& input": {
                  textTransform: "uppercase",
                  fontWeight: "bold",
                  padding: "12px 0",
                },
              },
              "& .MuiOutlinedInput-input::placeholder": {
                opacity: 0.7,
              },
            }}
          />

          <Button
            variant="contained"
            disabled={loading}
            onClick={handleSubmit}
            sx={{
              background: "#e4aa3e",
              color: "#fff",
              border: "none",
              borderRadius: "12px",
              padding: "12px 24px",
              marginRight: "4px",
              marginY: "4px",
              marginLeft: "8px",
              "&:hover": {
                background: "#5fa53a",
              },
              textTransform: "none",
              fontWeight: "bold",
              fontSize: "14px",
              whiteSpace: "nowrap",
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Rechercher"
            )}
          </Button>
        </Box>
      </Box>
      <Typography
        variant="subtitle1"
        sx={{
          color: "#555",
          padding: "8px 8px",
          fontWeight: "bold",
          fontSize: "14px",
        }}
      >
        Ou contacter l'Agrac ou le commissaire de justice.
      </Typography>
      <Typography color={"#d32f2f"} variant="body2" sx={{ mt: 1, minHeight: 20, ml: 1 }}>
        {errors.referenceId ? "Veuillez saisir votre référence id" : ""}
      </Typography>

      {/* Drawer pour afficher le reçu */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleCloseDrawer}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: "70%" },
            maxWidth: "90vw",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        {/* Header du Drawer */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #eee",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Reçu de participation
          </Typography>
          <IconButton onClick={handleCloseDrawer} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Contenu du Drawer */}
        <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
          {infosParticipant && <BadgeA6 infosParticipant={infosParticipant} />}
        </Box>
      </Drawer>
    </Box>
  );
};